// One card per sub-feature, each carrying its reference.
//
// The overview is a door, not a report: every card opens the view that explains
// the figure on it. A card that cannot run and one that ran and found nothing
// are different answers, and the state says which.
import React from 'react';
import { FiArrowRight, FiLock } from 'react-icons/fi';
import s from '../AdsAnalytics.module.css';
import { money, num, pct } from '../format';

function Body({ card, currency }) {
  switch (card.key) {
    case 'overview': {
      if (card.state !== 'found') return null;
      const c = card.change || {};
      return (
        <ul className={s.cardList}>
          <li>
            <span className={s.cardTerm}>Spend</span>
            <span className={s.cardFigure}>
              {money(card.spend, currency)}{' '}
              {c.spend != null && (
                <span className={c.spend > 0 ? s.down : s.up}>
                  {c.spend > 0 ? '▲' : '▼'} {Math.abs(c.spend * 100).toFixed(0)}%
                </span>
              )}
            </span>
          </li>
          <li>
            <span className={s.cardTerm}>Ad sales</span>
            <span className={s.cardFigure}>
              {money(card.ad_sales, currency)}{' '}
              {c.ad_sales != null && (
                <span className={c.ad_sales >= 0 ? s.up : s.down}>
                  {c.ad_sales >= 0 ? '▲' : '▼'} {Math.abs(c.ad_sales * 100).toFixed(0)}%
                </span>
              )}
            </span>
          </li>
          <li>
            <span className={s.cardTerm}>ROAS</span>
            <span className={s.cardFigure}>{card.roas == null ? '—' : `${card.roas}×`}</span>
          </li>
          {card.by_type?.length > 1 && card.by_type.map((t) => (
            <li key={t.ad_product} className={s.cardNote}>
              {t.ad_product.replace('SPONSORED_', '').toLowerCase()}: {pct(t.spend_share)} of
              spend, {pct(t.sales_share)} of sales{t.skewed ? ' — taking more than it returns' : ''}
            </li>
          ))}
        </ul>
      );
    }
    case 'drill':
      if (card.state !== 'found') return null;
      return (
        <ul className={s.cardList}>
          <li><span className={s.cardTerm}>Spending without returning</span>
            <span className={s.cardFigure}>{num(card.waste)}</span></li>
          <li><span className={s.cardTerm}>Search terms behind them</span>
            <span className={s.cardFigure}>{num(card.terms)}</span></li>
          {card.no_impressions > 0 && (
            <li><span className={s.cardTerm}>Keywords with no impressions</span>
              <span className={s.cardFigure}>{num(card.no_impressions)}</span></li>
          )}
        </ul>
      );
    case 'alerts':
      if (card.state !== 'found') return null;
      return (
        <ul className={s.cardList}>
          <li className={s.cardNote}>
            {card.high} need looking at now. Alerts are raised on finalised days only —
            Amazon revises the most recent ones.
          </li>
        </ul>
      );
    default:
      return null;
  }
}

export default function FeatureCards({ data, loading, onOpen }) {
  if (loading && !data) {
    return (
      <div className={s.cardGrid}>
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={s.cardSkeleton} />)}
      </div>
    );
  }
  if (!data?.cards?.length) return null;

  return (
    <div className={s.cardGrid}>
      {data.cards.map((card) => {
        const clickable = card.state !== 'unavailable';
        return (
          <section key={card.key}
                   className={`${s.card} ${s[`card_${card.state}`]} ${clickable ? s.cardOpen : ''}`}
                   onClick={clickable ? () => onOpen(card.tab) : undefined}
                   role={clickable ? 'button' : undefined}
                   tabIndex={clickable ? 0 : undefined}
                   onKeyDown={clickable ? (e) => {
                     if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(card.tab); }
                   } : undefined}>
            <header className={s.cardHead}>
              <span className={s.cardRef}>{card.ref}</span>
              {clickable ? <FiArrowRight className={s.cardGo} /> : <FiLock className={s.cardLock} />}
            </header>
            <h3 className={s.cardTitle}>{card.title}</h3>
            <p className={s.cardQuestion}>{card.question}</p>
            <div className={s.cardHeadline}>
              <span className={s.cardValue}>{card.headline}</span>
              {card.unit && <span className={s.cardUnit}>{card.unit}</span>}
            </div>
            <p className={s.cardDetail}>{card.detail}</p>
            <Body card={card} currency={data.currency} />
          </section>
        );
      })}
    </div>
  );
}
